/**
 * Layout para fin.axorial.mx. Drawer DaisyUI: sidebar visible en lg+ con
 * los módulos de finanzas; hamburguer/drawer en mobile. Cada item navega
 * cambiando `?tab=` en el query, igual que el panel original.
 */
import { useMemo, useState } from "react";
import { Outlet, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ExternalLink,
  LogOut,
  Moon,
  Sun,
  Monitor,
  Menu,
  PanelLeftClose,
  PieChart,
  BarChart3,
  Receipt,
  Building2,
  Truck,
  Tag,
  Calculator,
  LineChart,
  FolderArchive,
  Landmark,
  Upload,
} from "lucide-react";
import { useEffect } from "react";
import { api, ApiError, SESSION_EXPIRED_EVENT } from "@/lib/api";
import type { SessionUser } from "@axorial/shared";

type Theme = "light" | "dark" | "system";

function getStoredTheme(): Theme {
  if (typeof window === "undefined") return "system";
  const v = window.localStorage.getItem("axorial.theme");
  return v === "light" || v === "dark" ? v : "system";
}

function applyTheme(t: Theme) {
  const dark =
    t === "dark" || (t === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.setAttribute("data-theme", dark ? "axorial-dark" : "axorial");
  if (t === "system") window.localStorage.removeItem("axorial.theme");
  else window.localStorage.setItem("axorial.theme", t);
}

function ThemeSwitcher() {
  const [theme, setTheme] = useState<Theme>(getStoredTheme);
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);
  return (
    <div className="dropdown dropdown-end">
      <button
        type="button"
        tabIndex={0}
        className="btn btn-ghost btn-sm"
        aria-label="Tema"
        title="Tema"
      >
        {theme === "dark" ? (
          <Moon className="size-4" />
        ) : theme === "light" ? (
          <Sun className="size-4" />
        ) : (
          <Monitor className="size-4" />
        )}
      </button>
      <ul
        tabIndex={0}
        className="dropdown-content menu menu-sm bg-base-200 rounded-box z-10 w-44 p-2 shadow-lg mt-1"
      >
        {(
          [
            { key: "system", label: "Sistema", Icon: Monitor },
            { key: "light", label: "Claro", Icon: Sun },
            { key: "dark", label: "Oscuro", Icon: Moon },
          ] as const
        ).map(({ key, label, Icon }) => (
          <li key={key}>
            <button
              type="button"
              onClick={() => setTheme(key)}
              className={theme === key ? "bg-primary/15" : ""}
            >
              <Icon className="size-4" /> {label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Items del sidebar. Cada uno mapea a `?tab=<key>`; al click hacemos
 * navigate manteniendo el resto del search (period, account) intacto.
 * Orden curado: lo más consultado arriba (resumen, transacciones,
 * cuentas), administrativo abajo (categorías, importar).
 */
const NAV: ReadonlyArray<{
  key: string;
  label: string;
  Icon: typeof PieChart;
}> = [
  { key: "resumen", label: "Resumen", Icon: PieChart },
  { key: "analisis", label: "Análisis", Icon: BarChart3 },
  { key: "transacciones", label: "Transacciones", Icon: Receipt },
  { key: "cuentas", label: "Cuentas", Icon: Building2 },
  { key: "proveedores", label: "Proveedores", Icon: Truck },
  { key: "categorias", label: "Categorías", Icon: Tag },
  { key: "calculadora", label: "Calculadora", Icon: Calculator },
  { key: "proyecciones", label: "Proyecciones", Icon: LineChart },
  { key: "archivero", label: "Archivero", Icon: FolderArchive },
  { key: "impuestos", label: "Impuestos", Icon: Landmark },
  { key: "importar", label: "Importar", Icon: Upload },
];

function Sidebar({ onItemClick }: { onItemClick?: () => void }) {
  // useSearch sin strict=false porque el único route que tiene search es
  // index ("/"). El cast a never lo necesitamos porque el tipo derivado
  // del routeTree espera un id de route concreto.
  const search = useSearch({ strict: false }) as { tab?: string };
  const activeTab = search.tab ?? "resumen";
  const navigate = useNavigate();
  return (
    <aside className="w-60 shrink-0 bg-base-200/60 border-r border-base-300/60 h-full overflow-y-auto py-4 px-3">
      <Link
        to="/"
        className="inline-flex items-center gap-2.5 px-3 mb-4 group"
        onClick={onItemClick}
      >
        <img src="/logo/isotipo.png" alt="" className="size-7" />
        <div className="leading-tight">
          <div className="text-sm font-semibold tracking-tight">Axorial</div>
          <div className="text-[10px] uppercase tracking-wider text-base-content/55">Finanzas</div>
        </div>
      </Link>
      <nav className="flex flex-col gap-0.5">
        {NAV.map((item) => {
          const isActive = activeTab === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => {
                navigate({
                  to: "/",
                  search: (prev) => ({ ...prev, tab: item.key }) as never,
                });
                onItemClick?.();
              }}
              className={
                "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors " +
                (isActive
                  ? "bg-primary/15 text-primary font-medium"
                  : "text-base-content/75 hover:bg-base-300/60 hover:text-base-content")
              }
            >
              <item.Icon className="size-4 shrink-0" />
              {item.label}
            </button>
          );
        })}
      </nav>
      <div className="mt-6 pt-4 border-t border-base-300/60 px-1">
        <a
          href="https://panel.axorial.mx"
          className="flex items-center gap-2 px-2 py-1.5 text-xs text-base-content/60 hover:text-base-content"
        >
          <ExternalLink className="size-3.5" /> Panel operacional
        </a>
      </div>
    </aside>
  );
}

/**
 * Aviso bloqueante cuando la sesión de Cloudflare Access expiró (el
 * cliente HTTP lo detecta y emite SESSION_EXPIRED_EVENT). Sin esto, cada
 * acción falla con un críptico "Failed to fetch" y el usuario no sabe que
 * basta recargar para re-autenticarse con Google.
 */
function SessionExpiredOverlay() {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    const onExpired = () => setExpired(true);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);
  if (!expired) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="card-elevated bg-base-100 rounded-xl p-6 max-w-sm w-full space-y-3 text-center">
        <div className="font-display font-semibold text-lg">Tu sesión expiró</div>
        <p className="text-sm text-base-content/70">
          Por seguridad la sesión dura 24 horas. Recarga para volver a entrar con tu cuenta de
          Google — lo que no habías guardado se va a perder.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="btn btn-primary w-full"
        >
          Volver a entrar
        </button>
      </div>
    </div>
  );
}

export function AppShell() {
  const qc = useQueryClient();
  // Shape plano (`{...user, devMode}`) — igual al panel-api/me. Si el
  // worker rechaza con 403 (rol fuera de admin|finanzas) la consulta
  // entra en error y abajo mostramos la pantalla "sin acceso".
  const meQuery = useQuery<SessionUser & { devMode?: boolean }, ApiError>({
    queryKey: ["me"],
    queryFn: () => api.get("/me"),
    staleTime: 5 * 60_000,
    // Sin retry para 403 — el rol no va a cambiar entre intentos.
    retry: (_, err) => {
      if (err instanceof ApiError && err.status === 403) return false;
      return false;
    },
  });
  const me = meQuery.data;

  const stopImpersonate = useMutation({
    mutationFn: () => api.del("/admin/impersonate"),
    onSuccess: () => {
      qc.clear();
      window.location.href = "https://panel.axorial.mx/";
    },
  });

  const isImpersonating = !!me?.impersonatedBy;

  // Mobile drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = useMemo(() => () => setDrawerOpen(false), []);

  // Pantalla "sin acceso": cuando el rol del user no es admin|finanzas el
  // worker fin-api responde 403 a /api/me (vía accessAuth). Render full
  // screen sin sidebar para no confundir — el menú no aplica si no podés
  // entrar a ningún módulo.
  if (meQuery.error instanceof ApiError && meQuery.error.status === 403) {
    return (
      <div className="min-h-screen grid place-items-center bg-base-100 text-base-content px-6">
        <div className="max-w-md w-full text-center space-y-6">
          <img src="/logo/isotipo.png" alt="" className="size-14 mx-auto opacity-90" />
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight">Sin acceso a Finanzas</h1>
            <p className="text-sm text-base-content/70 leading-relaxed">
              Tu cuenta no tiene permisos para entrar a este módulo.
            </p>
            <p className="text-xs text-base-content/55 mt-3">
              Si crees que debería tener acceso, contactá a un administrador.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <a href="https://panel.axorial.mx" className="btn btn-primary btn-sm">
              Ir al panel operacional
            </a>
            <a href="/cdn-cgi/access/logout" className="btn btn-ghost btn-sm">
              Cerrar sesión
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-base-100 text-base-content">
      <SessionExpiredOverlay />
      {isImpersonating && me && (
        <div className="bg-warning text-warning-content px-4 py-2 text-sm flex items-center justify-between gap-3">
          <span>
            Viendo como <strong>{me.name}</strong> ({me.email}). Solo lectura.
          </span>
          <button
            type="button"
            onClick={() => stopImpersonate.mutate()}
            className="btn btn-xs btn-ghost"
          >
            Salir
          </button>
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        {/* Sidebar fija en lg+. En mobile vive en un drawer toggleable. */}
        <div className="hidden lg:block">
          <Sidebar />
        </div>
        {drawerOpen && (
          <div className="lg:hidden fixed inset-0 z-40 flex">
            <div className="absolute inset-0 bg-black/40" onClick={closeDrawer} aria-hidden />
            <div className="relative z-50 h-full">
              <Sidebar onItemClick={closeDrawer} />
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col min-w-0">
          <header className="sticky top-0 z-30 bg-base-100/80 backdrop-blur border-b border-base-300/60">
            <div className="px-4 md:px-6 py-3 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => setDrawerOpen((v) => !v)}
                className="btn btn-ghost btn-sm lg:hidden"
                aria-label="Abrir menú"
              >
                {drawerOpen ? <PanelLeftClose className="size-4" /> : <Menu className="size-4" />}
              </button>
              <div className="text-sm text-base-content/55 hidden md:block">fin.axorial.mx</div>
              <div className="flex items-center gap-1 ml-auto">
                <ThemeSwitcher />
                {me && (
                  <div className="ml-2 hidden sm:flex flex-col items-end leading-tight">
                    <span className="text-xs font-medium">{me.name}</span>
                    <span className="text-[10px] text-base-content/55">{me.email}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    window.location.href = "/cdn-cgi/access/logout";
                  }}
                  className="btn btn-ghost btn-sm ml-1"
                  title="Cerrar sesión"
                >
                  <LogOut className="size-4" />
                </button>
              </div>
            </div>
          </header>

          <main className="flex-1 px-4 md:px-6 py-6 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
